import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Send, Mic } from 'lucide-react'
import { MessageList } from './MessageList'
import { ChatInput } from './ChatInput'
import { TypingIndicator } from './TypingIndicator'
import { TaskTracker } from '@/components/tasks/TaskTracker'
import { VoiceButton } from '@/components/voice/VoiceButton'
import { NikiAvatar } from '@/components/niki/NikiAvatar'
import { AbilitiesShowcase } from '@/components/abilities'
import { AuthorizationModal } from '@/components/auth/AuthorizationModal'
import { useChat } from '@/hooks/useChat'
import { useTaskProgress } from '@/hooks/useTaskProgress'
import { useAuthorization } from '@/hooks/useAuthorization'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { cn } from '@/utils'

export function ChatContainer() {
  const {
    messages,
    isTyping,
    error,
    sendMessage,
    retryMessage,
    setError,
  } = useChat()

  const { activeQueue, hasActiveTask, clearActiveQueue } = useTaskProgress()
  const { pendingAuth, timeRemaining, approveAuth, denyAuth } = useAuthorization()
  const user = useAuthStore((s) => s.user)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(messages.length)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  // Clear TaskTracker when a new assistant message arrives
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage?.role === 'assistant') {
        // Small delay to let user see the completed state
        setTimeout(() => {
          clearActiveQueue()
        }, 1500)
      }
    }
    prevMessageCountRef.current = messages.length
  }, [messages, clearActiveQueue])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 glass-light">
        <div className="flex items-center gap-3">
          <NikiAvatar size="sm" state={isTyping || hasActiveTask ? 'thinking' : 'idle'} showRipples={false} />
          <div>
            <h2 className="text-lg font-semibold text-white">Chat with Niki</h2>
            <p className="text-xs text-white/50">
              {hasActiveTask
                ? `Working: ${activeQueue?.steps.find((s) => s.status === 'in_progress')?.display_name || 'Processing...'}`
                : isTyping
                  ? 'Thinking...'
                  : 'Online'}
            </p>
          </div>
        </div>
        <VoiceButton />
      </div>

      {/* Messages Area with configurable background */}
      <ChatBackgroundWrapper>
        {messages.length === 0 ? (
          <WelcomeMessage userName={user?.name} onSuggestionClick={sendMessage} />
        ) : (
          <MessageList messages={messages} onRetry={retryMessage} />
        )}

        {/* Task Progress Tracker */}
        <AnimatePresence>
          {hasActiveTask && <TaskTracker queue={activeQueue} />}
        </AnimatePresence>

        <AnimatePresence>
          {isTyping && !hasActiveTask && <TypingIndicator />}
        </AnimatePresence>

        {/* Error Toast */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="fixed bottom-28 left-1/2 -translate-x-1/2 px-5 py-3 rounded-xl flex items-center gap-3 z-50"
              style={{
                background: 'rgba(239, 68, 68, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(239, 68, 68, 0.4)',
              }}
            >
              <span className="text-sm text-white">{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-white/80 hover:text-white text-lg font-bold"
              >
                &times;
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </ChatBackgroundWrapper>

      {/* Input Area */}
      <ChatInput onSend={(msg, attachments) => sendMessage(msg, attachments)} disabled={isTyping} />

      {/* Authorization Modal */}
      <AuthorizationModal
        pendingAuth={pendingAuth}
        timeRemaining={timeRemaining}
        onApprove={approveAuth}
        onDeny={denyAuth}
      />
    </div>
  )
}

interface WelcomeMessageProps {
  userName?: string
  onSuggestionClick: (message: string) => void
}

function WelcomeMessage({ userName, onSuggestionClick }: WelcomeMessageProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4"
    >
      {/* Animated Avatar */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
      >
        <NikiAvatar size="hero" state="idle" />
      </motion.div>

      {/* Welcome Text */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 mb-6"
      >
        <h2 className="text-3xl font-bold text-white mb-3">
          Hello{userName ? `, ${userName.split(' ')[0]}` : ''}!
        </h2>
        <p className="text-lg text-white/60 max-w-md flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          I'm Niki, your AI digital employee
          <Sparkles className="w-5 h-5 text-primary" />
        </p>
        <p className="text-white/40 mt-2">
          What can I help you with?
        </p>
      </motion.div>

      {/* Abilities Showcase */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-lg"
      >
        <AbilitiesShowcase onTryAbility={onSuggestionClick} />
      </motion.div>

      {/* Voice tip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0 }}
        className="mt-6 glass-card px-4 py-3 flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
          <Mic className="w-4 h-4 text-primary" />
        </div>
        <div className="text-left">
          <p className="text-sm text-white/80">Voice-enabled</p>
          <p className="text-xs text-white/40">Tap the mic and speak naturally</p>
        </div>
      </motion.div>

      {/* Quick tip */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-6 text-xs text-white/30 flex items-center gap-2"
      >
        <Send className="w-3 h-3" />
        Type a message, tap a suggestion, or just speak
      </motion.p>
    </motion.div>
  )
}

// Chat Background Wrapper - applies the selected background style with dynamic colors
function ChatBackgroundWrapper({ children }: { children: React.ReactNode }) {
  const { chatBackground, colorScheme } = useUIStore()
  
  // Color scheme configurations for chat backgrounds
  const colorConfigs: Record<string, { bg: string; pattern: string; patternLight: string }> = {
    green: {
      bg: 'linear-gradient(135deg, #0F3D1A 0%, #134422 50%, #0A2B12 100%)',
      pattern: '#4CD137',
      patternLight: '#72E05B',
    },
    blue: {
      bg: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
      pattern: '#3B82F6',
      patternLight: '#60A5FA',
    },
    purple: {
      bg: 'linear-gradient(135deg, #1E1033 0%, #2D1F4D 50%, #1E1033 100%)',
      pattern: '#8B5CF6',
      patternLight: '#A78BFA',
    },
    orange: {
      bg: 'linear-gradient(135deg, #1C1208 0%, #3D2A0F 50%, #1C1208 100%)',
      pattern: '#F59E0B',
      patternLight: '#FBBF24',
    },
    pink: {
      bg: 'linear-gradient(135deg, #1F0A14 0%, #3D1528 50%, #1F0A14 100%)',
      pattern: '#EC4899',
      patternLight: '#F472B6',
    },
    classic: {
      bg: 'linear-gradient(135deg, #D9D2CA 0%, #E4DCD4 50%, #D9D2CA 100%)',
      pattern: '#5BA057',
      patternLight: '#7AC277',
    },
  }
  
  const colors = colorConfigs[colorScheme] || colorConfigs.green
  const isClassic = colorScheme === 'classic'
  
  // Generate dynamic styles based on background type and color scheme
  const getBackgroundStyle = () => {
    const patternSvg = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${encodeURIComponent(colors.pattern)}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    
    switch (chatBackground) {
      case 'pattern':
        return {
          background: colors.bg,
          '--pattern-url': patternSvg,
        } as React.CSSProperties
      case 'gradient':
        return { background: colors.bg }
      case 'solid':
        return { background: isClassic ? '#E4DCD4' : '#0F172A' }
      case 'waves':
        return { background: colors.bg }
      case 'dots':
        return { background: colors.bg }
      default:
        return { background: colors.bg }
    }
  }
  
  return (
    <div 
      className={cn(
        'flex-1 overflow-y-auto px-4 py-6 scrollbar-hide relative',
        chatBackground === 'pattern' && 'chat-bg-pattern',
        chatBackground === 'waves' && 'chat-bg-waves', 
        chatBackground === 'dots' && 'chat-bg-dots',
        isClassic && 'classic-theme'
      )}
      style={getBackgroundStyle()}
    >
      {/* Pattern overlay */}
      {chatBackground === 'pattern' && (
        <div 
          className="absolute inset-0 z-0 opacity-12 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${encodeURIComponent(colors.pattern)}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      )}
      {chatBackground === 'dots' && (
        <div 
          className="absolute inset-0 z-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, ${colors.pattern} 1px, transparent 1px)`,
            backgroundSize: '16px 16px',
          }}
        />
      )}
      {chatBackground === 'waves' && (
        <div 
          className="absolute inset-0 z-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='20' viewBox='0 0 100 20'%3E%3Cpath d='M0 10 Q25 0 50 10 T100 10' fill='none' stroke='${encodeURIComponent(colors.pattern)}' stroke-width='2'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 20px',
          }}
        />
      )}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  )
}
